import { BookMarked, Brain, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Header } from '@/components/shared/header'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useGoBack } from '@/hooks/useGoBack'
import { useFlashcardStore } from '@/store/useFlashcardStore'

import { CardList } from './card-list'
import { ReviewSession } from './review-session'

type Tab = 'list' | 'review'

export const FlashcardReviewPage = () => {
  const goBack = useGoBack()
  const [activeTab, setActiveTab] = useState<Tab>('review')
  const { reviewCards, stats, fetchReviewCards, fetchStats, fetchCards, isLoading } = useFlashcardStore()

  useEffect(() => {
    void fetchReviewCards()
    void fetchStats()
    void fetchCards()
  }, [fetchReviewCards, fetchStats, fetchCards])

  return (
    <div className="flex flex-col h-screen">
      <Header title="単語帳" onClose={goBack} />

      {/* Stats bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b">
        {stats ? (
          <>
            <Badge variant="secondary" className="gap-1">
              <BookMarked className="w-3 h-3" />
              {stats.total}
            </Badge>
            <Badge variant="default" className="gap-1">
              <Brain className="w-3 h-3" />
              今日: {stats.due_today}
            </Badge>
            <Badge variant="outline" className="gap-1 text-green-600">
              習得: {stats.mastered_count}
            </Badge>
            {stats.leech_count > 0 && (
              <Badge variant="destructive" className="gap-1">
                <RotateCcw className="w-3 h-3" />
                要注意: {stats.leech_count}
              </Badge>
            )}
          </>
        ) : (
          <Skeleton className="h-6 w-48" />
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('review')}
          className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
            activeTab === 'review' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
          }`}
        >
          レビュー ({reviewCards.length})
        </button>
        <button
          onClick={() => setActiveTab('list')}
          className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
            activeTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
          }`}
        >
          カード一覧
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && reviewCards.length === 0 ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : activeTab === 'review' ? (
          reviewCards.length > 0 ? (
            <ReviewSession />
          ) : (
            <EmptyReview />
          )
        ) : (
          <CardList />
        )}
      </div>
    </div>
  )
}

const EmptyReview = () => (
  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
    <Card className="p-8 max-w-sm">
      <BookMarked className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold mb-2">今日のレビュー完了!</h3>
      <p className="text-sm text-muted-foreground">
        復習するカードがありません。新しいカードは英語ページで登録できます。
      </p>
    </Card>
  </div>
)
